import { shopifyProvinces } from "@redotech/locale/provinces";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import {
  AddressAutocompleteActions,
  AddressAutocompleteFields,
} from "@redotech/redo-web/address-autocomplete-form";
import { RedoMerchantRpcClientContext } from "../rpc-client";

type NameAutocompleteActions = Pick<
  AddressAutocompleteActions,
  "getAddressSuggestionsFromName" | "getAddressFromNameSuggestion"
>;

const normalizeProvince = (countryCode: string, province: string): string => {
  return (
    shopifyProvinces.find(
      (entry) =>
        entry.countryCode === countryCode &&
        (entry.provinceCode.toLowerCase() === province.toLowerCase() ||
          entry.province.toLowerCase() === province.toLowerCase()),
    )?.province ?? province
  );
};

const getAddressSuggestionsFromName: AddressAutocompleteActions["getAddressSuggestionsFromName"] =
  async () => [];

const getAddressFromNameSuggestion: AddressAutocompleteActions["getAddressFromNameSuggestion"] =
  async () => ({
    name: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    zip: "",
    countryCode: RedoShopifyCountryCode.Us,
  });

export const useAddressAutocompleteActions = (
  nameAutocompleteActions?: NameAutocompleteActions,
): AddressAutocompleteActions => {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string, countryCode?: string) => {
      const { suggestions } = await rpcClient.getAutocompleteSuggestions({
        query,
        sessionToken,
        countryCode,
      });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion.keyToFullObject,
        displayText: suggestion.displayText,
      }));
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      const { address } = await rpcClient.getAddressFromSuggestion({
        suggestionId,
      });
      if (!address) {
        return null;
      }

      return {
        ...address,
        province: normalizeProvince(address.countryCode, address.province),
      } as AddressAutocompleteFields;
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName:
      nameAutocompleteActions?.getAddressSuggestionsFromName ??
      getAddressSuggestionsFromName,
    getAddressFromNameSuggestion:
      nameAutocompleteActions?.getAddressFromNameSuggestion ??
      getAddressFromNameSuggestion,
  };
};
